import { useEffect, useState } from "react";
import "./index.css";
import InputForm from "./InputForm";
import InputPhone from "./InputPhone";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";

/* Options import */
import {
  adicionalOptions,
  alarmaOptions,
} from "./Options";
import { LabelInput } from "./LabelInput";
import { AnimatePresence, motion } from "framer-motion";

/* A/B Testing */
import { useFormOptimization } from "../hooks/useABTest";
import abTestingManager from "../utils/abTesting";
import useIpLocation from "../hooks/useIpLocation";

export default function AlarmQuoteModal() {
  // A/B Testing para optimización de formulario
  const { variant, config } = useFormOptimization();

  const urlHook = process.env.REACT_APP_HOOK_URL;
  const apiKey = process.env.REACT_APP_MAKE_API_KEY;

  // Validación de variables de entorno
  if (!urlHook) {
    console.error("REACT_APP_HOOK_URL no está configurada en las variables de entorno");
  }

  if (!apiKey) {
    console.error("REACT_APP_MAKE_API_KEY no está configurada en las variables de entorno");
  }

  const navigate = useNavigate();

  const valores = {
    1: {
      origen: "GG-Ads",
    },
    2: {
      origen: "FB-Ads",
    },
    3: {
      origen: "Mail-Marketing",
    },
    4: {
      origen: "tiktok-ads",
    },
    5: {
      origen: "MS-Ads",
    },
    default: {
      origen: "FB-Ads",
    }
  };

  const queryParamsSession = sessionStorage.getItem("queryParams");
  const queryParamsSessionParsed = queryParamsSession ? JSON.parse(queryParamsSession) : {};

  const [data, setData] = useState({
    alarma: "",
    accesos: "",
    name: "",
    adicional: "Si",
    phoneVeri: "",
    ipLocalidad: "",
    ipProvincia: "",
    telefonoValidado: false,
    localidad: "",
    email: "",
    origen:
      valores[queryParamsSessionParsed?.origen]?.origen || valores["default"].origen,
    medio: queryParamsSessionParsed?.medio || "",
    debug: queryParamsSessionParsed?.debug || 0,
    landing: window.location.origin + window.location.pathname,
    eventid: Date.now()
  });

  // Get IP localidad y provincia
  const { data: ipLocationData } = useIpLocation();

  useEffect(() => {
    if (ipLocationData) {
      setData(prevData => ({
        ...prevData,
        ipLocalidad: ipLocationData.city ?? "desconocida",
        ipProvincia: ipLocationData.region || "Desconocida"
      }));
    }
    console.log("IP Location Data:", ipLocationData);

  }, [ipLocationData]);

  const [step, setStep] = useState(1);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!variant) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'funnel_cotizador_start',
      form_variant: variant
    });
  }, [variant]);

  // Función para obtener el mapeo de pasos desde la configuración
  const getStepMapping = () => {
    return config.stepMapping || {};
  };

  // Función para obtener el número de pregunta desde la configuración
  const getQuestionNumber = (questionType) => {
    return config.questionNumbers?.[questionType] || step;
  };

  // Función para obtener el paso anterior
  const getPreviousStep = (currentStep) => {
    if (currentStep === 1) return 1;
    return currentStep - 1;
  };

  // Función para obtener el total de pasos desde la configuración
  const getTotalSteps = () => {
    return config.totalSteps || 6;
  };

  // Nuevo manejador para la tecla Enter
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const stepMapping = getStepMapping();
      const nextStep = stepMapping[step]?.nextStep || step;

      if (nextStep !== step) {
        stepChange(e, nextStep);
      }
    }
  };

  const checkDataStep = () => {
    const stepMapping = getStepMapping();
    const currentStepQuestions = stepMapping[step]?.questions || [];

    setError("");

    // Verificar cada pregunta requerida en el paso actual
    for (const question of currentStepQuestions) {
      switch (question) {
        case 'alarma':
          if (!data.alarma) {
            setError("Elegí una opción para la alarma");
            return false;
          }
          break;
        case 'accesos':
          if (data.alarma === "Casa" && !data.accesos) {
            setError("Elegí una opción para los accesos");
            return false;
          }
          break;
        case 'adicional':
          if (!data.adicional) {
            setError("Elegí una opción para cámaras adicionales");
            return false;
          }
          break;
        case 'localidad':
          if (!data.localidad) {
            setError("Introducí tu localidad");
            return false;
          }
          break;
        case 'name':
          if (!data.name) {
            setError("Verificá tu nombre");
            return false;
          }
          break;
        case 'phoneVeri':
          if (!data.phoneVeri) {
            setError("Verificá tu teléfono");
            return false;
          }
          if (!data.telefonoValidado) {
            setError("El teléfono no es válido");
            return false;
          }
          break;
        case 'email':
          // Email es opcional en algunos casos
          break;
      }
    }

    return true;
  };

  // Función modificada para permitir retroceder sin validación
  const stepChange = (e, to, direction = 'next') => {
    e.preventDefault();

    // Scroll al top
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });

    // Limpiar el error al navegar entre pasos
    setError("");

    // Solo validamos si vamos al siguiente paso, no si retrocedemos
    if (direction === 'next' && !checkDataStep()) {
      return false;
    }

    setStep(to);
  };

  const handleChangeInput = (e) => {
    setData({
      ...data,
      [e.target.name]: e.target.value
    });
  };

  /* Form Submit */
  const [formLoading, setFormLoading] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);

  const handleSubmit = async (e) => {
    setFormLoading(true);
    setError("");

    // Validar que las variables de entorno estén configuradas
    if (!urlHook || !apiKey) {
      setFormLoading(false);
      setError("Error de configuración. Contacte al administrador.");
      console.error("Variables de entorno no configuradas:", { urlHook: !!urlHook, apiKey: !!apiKey });
      return;
    }

    // Agregar datos de A/B testing
    const formDataWithABTest = {
      ...data,
      ...abTestingManager.getTestDataForWebhook('form_optimization'),
      ab_test_steps_completed: getTotalSteps()
    };

    await axios
      .post(urlHook, formDataWithABTest, {
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "x-make-apikey": apiKey
        }
      })
      .then(function (response) {
        //Success ----------------------------------------------------
        console.log("Form Success", response);

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'funnel_conversion',
          form_variant: variant,
          eventid: data.eventid,
          user_data: {
            email: data.email,
            phone_number: data.phoneVeri,
            address: {
              first_name: data.name,
            }
          }
        });

        try {
          // eslint-disable-next-line no-undef
          gtag("event", "Goal", {
            event_category: `Click Button Form`,
            event_label: "Form"
          });
        } catch (errorC) {
          console.log(errorC);
        }
        setFormSuccess(true);
        setTimeout(() => {
          navigate("/send");
        }, 3000);
      })
      //End Succes ----------------------------------------------------
      .catch(function (error) {
        //Error ----------------------------------------------------
        setFormLoading(false);

        try {
          // eslint-disable-next-line no-undef
          gtag("event", "Forms", {
            event_category: "Form Error",
            event_label: error
          });
        } catch (errorC) {
          console.log(errorC);
        }
        console.log(error);
        console.log("error un problema");
        setError("Hubo un problema al enviar los datos");
        setStep(4);
      });
    //End Error ----------------------------------------------------
  };

  useEffect(() => {
    if (step === "loader") {
      console.log("Step Loader!");
      handleSubmit();
    }
  }, [step]);

  // Configuración de campos de formulario
  const fieldConfigs = {
    alarma: {
      type: 'radio',
      label: '¿Dónde deberías instalar tu alarma?',
      placeholder: '',
      options: alarmaOptions,
      required: true,
      error: 'Debe seleccionar una opción'
    },
    accesos: {
      type: 'radio',
      label: 'Tu casa tiene <span>accesos por:</span>',
      placeholder: '',
      options: alarmaOptions[0]?.accesos || [],
      required: true,
      error: 'Debe seleccionar una opción',
      condition: () => data.alarma === "Casa"
    },
    adicional: {
      type: 'radio',
      label: '¿Te gustaría adicionar <span>cámaras a la cotización?</span>',
      placeholder: '',
      options: adicionalOptions,
      required: true,
      error: 'Debe seleccionar una opción'
    },
    name: {
      type: 'text',
      label: '¿Cómo es tu nombre?',
      placeholder: 'Ingresá tu Nombre',
      required: true,
      error: 'Debe introducir un nombre'
    },
    phoneVeri: {
      type: 'phone',
      label: 'Ingresá <span>Teléfono o Whatsapp</span>',
      description: 'Ingresa número con cod. de área ejemplo 1135656252',
      required: true,
      error: 'Debe introducir un teléfono válido'
    },
    localidad: {
      type: 'text',
      label: 'Tu <span>ciudad o localidad</span>',
      placeholder: 'Ingrese Localidad de Instalación',
      description: 'Verificación rápida de cobertura',
      required: true,
      error: 'Debes introducir una localidad/ciudad'
    },
    email: {
      type: 'email',
      label: 'Ingresá tu email',
      placeholder: 'Ingresa tu Email',
      description: 'Recibí la copia completa de tu cotización',
      required: false,
      error: 'Debe introducir un email válido',
      pattern: '[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$'
    }
  };

  // Renderizar un campo individual
  const renderField = (fieldName, groupTitle = null, customNumber = null) => {
    const fieldConfig = fieldConfigs[fieldName];
    if (!fieldConfig) return null;

    // Verificar condición si existe
    if (fieldConfig.condition && !fieldConfig.condition()) {
      return null;
    }

    const questionNumber = customNumber || getQuestionNumber(fieldName);

    if (fieldConfig.type === 'phone') {
      return (
        <div key={`${fieldName}-field`} style={{ marginTop: groupTitle ? '40px' : '0' }}>
          <LabelInput
            number={questionNumber}
            description={fieldConfig.description}
            required={fieldConfig.required}
          >
            <span dangerouslySetInnerHTML={{ __html: fieldConfig.label }} />
          </LabelInput>
          <InputPhone data={data} setData={setData} />
        </div>
      );
    }

    return (
      <div key={`${fieldName}-field`} style={{ marginTop: groupTitle ? '20px' : '0' }}>
        <LabelInput
          number={questionNumber}
          description={fieldConfig.description}
          required={fieldConfig.required}
        >
          <span dangerouslySetInnerHTML={{ __html: fieldConfig.label }} />
        </LabelInput>
        <InputForm
          type={fieldConfig.type}
          name={fieldName}
          value={data[fieldName]}
          placeholder={fieldConfig.placeholder}
          options={fieldConfig.options}
          handleChangeInput={handleChangeInput}
          error={fieldConfig.error}
          required={fieldConfig.required}
          pattern={fieldConfig.pattern}
        />
      </div>
    );
  };

  // Obtener configuración del paso desde A/B testing
  const getStepConfig = () => {
    const stepData = config.steps?.find(s => s.step === step);
    return stepData || { title: '', description: '', questions: [] };
  };

  // Renderizar contenido dinámico según el paso y variante
  const renderStepContent = () => {
    const stepMapping = getStepMapping();
    const currentStepQuestions = stepMapping[step]?.questions || [];
    const nextStep = stepMapping[step]?.nextStep;
    const prevStep = getPreviousStep(step);
    const stepConfig = getStepConfig();

    // Título y descripción del paso
    const stepHeader = step === 1 ? (
      <div key="step-header">
        <h1>
          Cotizá ahora tu <span>Alarma ADT Gratis</span>
        </h1>
        <p style={{ marginBottom: 70 }}>
          Seguí los pasos y conocé las mejores opciones disponibles
        </p>
      </div>
    ) : stepConfig.title ? (
      <div key="step-header">
        <h2>{stepConfig.title}</h2>
        {stepConfig.description && <p>{stepConfig.description}</p>}
      </div>
    ) : null;

    // Renderizar campos según configuración
    const renderFields = () => {
      // Caso especial para paso 1 (alarma + accesos)
      if (step === 1) {
        return [
          renderField('alarma', null, 1),
          renderField('accesos') // Se renderiza condicionalmente
        ].filter(Boolean);
      }

      // Para otros pasos, usar la configuración del A/B testing
      const fields = [];

      // Determinar si es un paso agrupado o individual
      const isGroupedStep = currentStepQuestions.length > 1;

      if (isGroupedStep) {
        // Casos especiales para pasos agrupados
        if (currentStepQuestions.includes('name') && currentStepQuestions.includes('phoneVeri')) {
          fields.push(
            <div key="grouped-contact">
              <LabelInput number={getQuestionNumber('name')} required>
                Datos de contacto
              </LabelInput>
              {renderField('name')}
              {renderField('phoneVeri', 'phone', getQuestionNumber('phoneVeri'))}
            </div>
          );
        } else if (currentStepQuestions.includes('localidad') && currentStepQuestions.includes('email')) {
          fields.push(
            <div key="grouped-final">
              <LabelInput number={getQuestionNumber('localidad')} required>
                Finalizar datos
              </LabelInput>
              {renderField('localidad')}
              {renderField('email', 'email', getQuestionNumber('email'))}
            </div>
          );
        } else {
          // Caso genérico para otros agrupamientos
          currentStepQuestions.forEach(questionName => {
            fields.push(renderField(questionName));
          });
        }
      } else {
        // Paso individual
        currentStepQuestions.forEach(questionName => {
          fields.push(renderField(questionName));
        });
      }

      return fields.filter(Boolean);
    };

    return (
      <motion.div
        className="flex flexCol"
        initial={{
          y: "50%",
          opacity: 0
        }}
        animate={{
          y: 0,
          opacity: 1
        }}
      >
        {/* Header del paso */}
        {stepHeader}

        {/* Campos del formulario */}
        {renderFields()}

        {/* Navegación */}
        <div className="navStep">
          {step === 1 ? (
            <Link className="backButton" to="/">
              <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 13L1 7L7 1" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Volver
            </Link>
          ) : (
            <button
              className="backButton"
              onClick={(e) => stepChange(e, prevStep, 'prev')}
            >
              <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 13L1 7L7 1" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Atrás
            </button>
          )}

          <button
            className="buttonForm btnnext"
            onClick={(e) => stepChange(e, nextStep)}
          >
            {nextStep === 'loader' ? 'Recibir cotización' : 'Siguiente'}
          </button>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="container-lg popBox">
      <div className="containerPop activePop">
        {
          (step !== "loader") &&
          <div className="stepView">
            {[...Array(getTotalSteps())].map((_, index) => {
              const stepNumber = index + 1;
              return (
                <div key={stepNumber} className={step >= stepNumber ? "step active" : "step"}>
                  <span />
                  <p>Paso {stepNumber}</p>
                </div>
              );
            })}
          </div>
        }
        <div className="popUp">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              className="formContainer"
            >
              <form
                onSubmit={handleSubmit}
                className={"step" + step + " form formContainer"}
                noValidate
                onKeyDown={handleKeyDown}
              >
                {step !== "loader" && renderStepContent()}

                {step === "loader" && (
                  <motion.div
                    className="flex flexCol"
                    initial={{
                      y: "50%",
                      opacity: 0
                    }}
                    animate={{
                      y: 0,
                      opacity: 1
                    }}
                  >
                    <h2>Enviando datos..</h2>
                    <p>
                      Estamos procesando tu solicitud
                    </p>
                  </motion.div>
                )}
              </form>
              {error && (
                <span className="error">
                  <svg
                    viewBox="0 0 22 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21.5474 14.3026L13.8774 1.58262C13.5673 1.09744 13.14 0.698147 12.6349 0.421562C12.1298 0.144976 11.5632 0 10.9874 0C10.4116 0 9.84499 0.144976 9.33992 0.421562C8.83485 0.698147 8.40755 1.09744 8.0974 1.58262L0.427405 14.3026C0.156458 14.7543 0.00919365 15.2693 0.00041668 15.7959C-0.00836029 16.3225 0.12166 16.8422 0.377405 17.3026C0.673084 17.8209 1.10106 18.2514 1.61761 18.55C2.13415 18.8487 2.72073 19.0049 3.3174 19.0026H18.6574C19.2502 19.0089 19.8341 18.8588 20.3503 18.5674C20.8665 18.2759 21.2966 17.8534 21.5974 17.3426C21.8607 16.8774 21.9948 16.3503 21.986 15.8158C21.9773 15.2814 21.8258 14.7589 21.5474 14.3026V14.3026ZM19.8474 16.3526C19.7272 16.5592 19.5528 16.729 19.343 16.8436C19.1333 16.9581 18.8962 17.0131 18.6574 17.0026H3.3174C3.07863 17.0131 2.84152 16.9581 2.63176 16.8436C2.42201 16.729 2.24759 16.5592 2.1274 16.3526C2.03964 16.2006 1.99343 16.0282 1.99343 15.8526C1.99343 15.6771 2.03964 15.5046 2.1274 15.3526L9.8074 2.62262C9.94541 2.44033 10.1238 2.29249 10.3285 2.1907C10.5332 2.08891 10.7588 2.03593 10.9874 2.03593C11.216 2.03593 11.4416 2.08891 11.6463 2.1907C11.851 2.29249 12.0294 2.44033 12.1674 2.62262L19.8374 15.3426C19.9286 15.4949 19.9775 15.6688 19.9793 15.8463C19.9811 16.0238 19.9355 16.1985 19.8474 16.3526V16.3526ZM10.9873 15.0026C11.5396 15.0026 11.9873 14.5548 11.9873 14.0026C11.9873 13.4503 11.5396 13.0026 10.9873 13.0026C10.435 13.0026 9.9873 13.4503 9.9873 14.0026C9.9873 14.5548 10.435 15.0026 10.9873 15.0026Z"
                      fill="#0060a9"
                    />
                    <path
                      d="M10.9873 6.00256C10.7221 6.00256 10.4677 6.10792 10.2802 6.29546C10.0927 6.48299 9.9873 6.73735 9.9873 7.00256V11.0026C9.9873 11.2678 10.0927 11.5221 10.2802 11.7097C10.4677 11.8972 10.7221 12.0026 10.9873 12.0026C11.2525 12.0026 11.5069 11.8972 11.6944 11.7097C11.8819 11.5221 11.9873 11.2678 11.9873 11.0026V7.00256C11.9873 6.73735 11.8819 6.48299 11.6944 6.29546C11.5069 6.10792 11.2525 6.00256 10.9873 6.00256Z"
                      fill="#0060a9"
                    />
                  </svg>
                  {error}
                </span>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
